import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurants, useUpdateRestaurant } from '@/hooks/useRestaurants';
import {
  useTables,
  useCreateTable,
  useCreateMultipleTables,
  useUpdateTable,
  useDeleteTable,
} from '@/hooks/useTables';
import { useZones } from '@/hooks/useZones';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pencil, Trash2, Plus, Save, X, Utensils, Users, Package } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { KitchenSpecialtiesSection } from '@/components/settings/KitchenSpecialtiesSection';
import { ZonesSection } from '@/components/settings/ZonesSection';
import { AssistantsSection } from '@/components/settings/AssistantsSection';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { useCurrentUser } from '@/hooks/useAuth';
import { useAllowNegativeStock, useUpdateAllowNegativeStock } from '@/hooks/useAllowNegativeStock';
import type { RestaurantTable } from '@/types/entities';

export function Settings() {
  const { t, i18n } = useTranslation();
  const { data: restaurants, isLoading: isLoadingRestaurant } = useRestaurants();
  const { selectedRestaurantId } = useRestaurantContext();
  const { data: currentUser } = useCurrentUser();

  const restaurant = restaurants?.[0];
  const restaurantId = restaurant?.id || selectedRestaurantId || '';

  const { data: tables, isLoading: isLoadingTables } = useTables(restaurantId);
  const { data: zones } = useZones(restaurantId);
  const updateRestaurant = useUpdateRestaurant(restaurantId);
  const { data: allowNegativeStock } = useAllowNegativeStock(restaurantId);
  const updateAllowNegativeStock = useUpdateAllowNegativeStock(restaurantId);
  const createTable = useCreateTable(restaurantId);
  const createMultipleTables = useCreateMultipleTables(restaurantId);
  const updateTable = useUpdateTable(restaurantId);
  const deleteTable = useDeleteTable(restaurantId);

  // Restaurant form state
  const [restaurantForm, setRestaurantForm] = useState({
    name: restaurant?.name || '',
    addressAr: restaurant?.addressAr || '',
    addressFr: restaurant?.addressFr || '',
    phone: restaurant?.phone || '',
  });
  const [isEditingRestaurant, setIsEditingRestaurant] = useState(false);

  // Table form state
  const [isAddingTable, setIsAddingTable] = useState(false);
  const [isAddingMultipleTables, setIsAddingMultipleTables] = useState(false);
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [tableForm, setTableForm] = useState({ tableNumber: '', seats: '', zoneId: '' });
  const [bulkTableForm, setBulkTableForm] = useState({
    startNumber: '',
    endNumber: '',
    seats: '',
    zoneId: '',
  });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  // Update form when restaurant data loads
  if (restaurant && !isEditingRestaurant && restaurantForm.name === '') {
    setRestaurantForm({
      name: restaurant.name,
      addressAr: restaurant.addressAr || '',
      addressFr: restaurant.addressFr || '',
      phone: restaurant.phone || '',
    });
  }

  const handleSaveRestaurant = async () => {
    await updateRestaurant.mutateAsync({
      name: restaurantForm.name,
      addressAr: restaurantForm.addressAr,
      addressFr: restaurantForm.addressFr,
      phone: restaurantForm.phone,
    });
    setIsEditingRestaurant(false);
  };

  const handleCancelRestaurantEdit = () => {
    setRestaurantForm({
      name: restaurant?.name || '',
      addressAr: restaurant?.addressAr || '',
      addressFr: restaurant?.addressFr || '',
      phone: restaurant?.phone || '',
    });
    setIsEditingRestaurant(false);
  };

  const handleAddTable = async () => {
    if (!tableForm.tableNumber || !tableForm.seats) return;

    try {
      await createTable.mutateAsync({
        tableNumber: parseInt(tableForm.tableNumber),
        seats: parseInt(tableForm.seats),
        zoneId: tableForm.zoneId || null,
      });

      setTableForm({ tableNumber: '', seats: '', zoneId: '' });
      setIsAddingTable(false);
    } catch (error: any) {
      if (error?.message === 'DUPLICATE_TABLE_NUMBER') {
        setAlertDialog({
          open: true,
          title: t('settings.duplicateTableNumber') || 'Duplicate Table Number',
          description:
            t('settings.duplicateTableNumberDescription', { number: tableForm.tableNumber }) ||
            `Table number ${tableForm.tableNumber} already exists. Please choose a different number.`,
        });
      } else {
        setAlertDialog({
          open: true,
          title: t('common.error') || 'Error',
          description:
            error?.message ||
            t('settings.createTableError') ||
            'Failed to create table. Please try again.',
        });
      }
    }
  };

  const handleAddMultipleTables = async () => {
    if (!bulkTableForm.startNumber || !bulkTableForm.endNumber || !bulkTableForm.seats) return;

    const startNum = parseInt(bulkTableForm.startNumber);
    const endNum = parseInt(bulkTableForm.endNumber);

    if (startNum > endNum) {
      setAlertDialog({
        open: true,
        title: t('settings.invalidTableRange') || 'Invalid Table Range',
        description:
          t('settings.invalidTableRangeDescription') ||
          'Start number must be less than or equal to end number',
      });
      return;
    }

    try {
      await createMultipleTables.mutateAsync({
        startNumber: startNum,
        endNumber: endNum,
        seats: parseInt(bulkTableForm.seats),
        zoneId: bulkTableForm.zoneId || null,
      });

      setBulkTableForm({ startNumber: '', endNumber: '', seats: '', zoneId: '' });
      setIsAddingMultipleTables(false);
    } catch (error: any) {
      if (error?.message === 'DUPLICATE_TABLE_NUMBER') {
        setAlertDialog({
          open: true,
          title: t('settings.duplicateTableNumber') || 'Duplicate Table Number',
          description:
            t('settings.duplicateTableNumberRangeDescription') ||
            `One or more table numbers in the range ${startNum}-${endNum} already exist. Please choose a different range.`,
        });
      } else {
        setAlertDialog({
          open: true,
          title: t('common.error') || 'Error',
          description:
            error?.message ||
            t('settings.createTableError') ||
            'Failed to create tables. Please try again.',
        });
      }
    }
  };

  const handleEditTable = (table: RestaurantTable) => {
    setEditingTableId(table.id);
    setTableForm({
      tableNumber: table.tableNumber.toString(),
      seats: table.seats.toString(),
      zoneId: table.zoneId || '',
    });
  };

  const handleSaveTable = async (tableId: string) => {
    if (!tableForm.tableNumber || !tableForm.seats) return;

    await updateTable.mutateAsync({
      id: tableId,
      tableNumber: parseInt(tableForm.tableNumber),
      seats: parseInt(tableForm.seats),
      zoneId: tableForm.zoneId || null,
    });

    setEditingTableId(null);
    setTableForm({ tableNumber: '', seats: '', zoneId: '' });
  };

  const handleCancelTableEdit = () => {
    setEditingTableId(null);
    setTableForm({ tableNumber: '', seats: '', zoneId: '' });
  };

  const handleDeleteTable = (tableId: string) => {
    setConfirmDialog({
      open: true,
      title: t('settings.confirmDeleteTable') || 'Delete Table',
      description:
        t('settings.confirmDeleteTableDescription') ||
        'Are you sure you want to delete this table? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteTable.mutateAsync(tableId);
        } catch (error: any) {
          if (error?.message === 'TABLE_IN_USE') {
            setAlertDialog({
              open: true,
              title: t('settings.tableInUseTitle') || 'Table In Use',
              description:
                t('settings.tableInUseDescription') ||
                'This table cannot be deleted because it has associated orders. Please complete or cancel all orders for this table first.',
            });
          } else {
            setAlertDialog({
              open: true,
              title: t('common.error') || 'Error',
              description:
                error?.message ||
                t('settings.deleteTableError') ||
                'Failed to delete table. Please try again.',
            });
          }
        }
      },
    });
  };

  if (isLoadingRestaurant) {
    return (
      <ProtectedRoute requiredRole="admin" adminOnly>
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="admin" adminOnly>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('settings.title')}</h1>
            <p className="text-muted-foreground">{t('settings.description')}</p>
          </div>

          <div className="space-y-8">
            {/* Restaurant Information Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('restaurant.information') || 'Restaurant Information'}</CardTitle>
                  {!isEditingRestaurant && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingRestaurant(true)}
                    >
                      <Pencil className="w-4 h-4 sm:me-2" />
                      <span className="hidden sm:inline">{t('common.edit')}</span>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('restaurant.name')}</Label>
                    <Input
                      id="name"
                      value={restaurantForm.name}
                      onChange={(e) =>
                        setRestaurantForm({ ...restaurantForm, name: e.target.value })
                      }
                      disabled={!isEditingRestaurant}
                      className={!isEditingRestaurant ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('restaurant.phone')}</Label>
                    <Input
                      id="phone"
                      value={restaurantForm.phone}
                      onChange={(e) =>
                        setRestaurantForm({ ...restaurantForm, phone: e.target.value })
                      }
                      disabled={!isEditingRestaurant}
                      className={!isEditingRestaurant ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="addressAr">{t('restaurant.addressAr')}</Label>
                    <Input
                      id="addressAr"
                      value={restaurantForm.addressAr}
                      onChange={(e) =>
                        setRestaurantForm({ ...restaurantForm, addressAr: e.target.value })
                      }
                      disabled={!isEditingRestaurant}
                      className={!isEditingRestaurant ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="addressFr">{t('restaurant.addressFr')}</Label>
                    <Input
                      id="addressFr"
                      value={restaurantForm.addressFr}
                      onChange={(e) =>
                        setRestaurantForm({ ...restaurantForm, addressFr: e.target.value })
                      }
                      disabled={!isEditingRestaurant}
                      className={!isEditingRestaurant ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                {isEditingRestaurant && (
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={handleCancelRestaurantEdit}
                      disabled={updateRestaurant.isPending}
                    >
                      <X className="w-4 h-4 sm:me-2" />
                      <span className="hidden sm:inline">{t('common.cancel')}</span>
                    </Button>
                    <Button onClick={handleSaveRestaurant} disabled={updateRestaurant.isPending}>
                      <Save className="w-4 h-4 sm:me-2" />
                      <span className="hidden sm:inline">{t('common.save')}</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inventory Settings Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  <CardTitle>{t('settings.inventory.title') || 'Inventory Settings'}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="allow-negative-stock">
                      {t('settings.inventory.allowNegativeStock') || 'Allow Negative Stock'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.inventory.allowNegativeStockDescription') || 'When enabled, servers can order items even when stock is insufficient. Stock quantities can go negative.'}
                    </p>
                  </div>
                  <Switch
                    id="allow-negative-stock"
                    checked={allowNegativeStock ?? false}
                    onCheckedChange={(checked) => {
                      updateAllowNegativeStock.mutate(checked);
                    }}
                    disabled={updateAllowNegativeStock.isPending}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Kitchen Specialties Section */}
            <KitchenSpecialtiesSection restaurantId={restaurantId} />

            {/* Zones Section */}
            <ZonesSection restaurantId={restaurantId} />

            {/* Assistants Section */}
            <AssistantsSection restaurantId={restaurantId} />

            {/* Push Notifications Section */}
            <NotificationSettings
              restaurantId={restaurantId}
              userId={currentUser?.id}
            />

            {/* Table Management Section */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>{t('settings.tableManagement') || 'Table Management'}</CardTitle>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsAddingTable(true);
                        setIsAddingMultipleTables(false);
                      }}
                      disabled={isAddingTable || isAddingMultipleTables}
                      className="w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4 me-2" />
                      <span className="truncate">{t('settings.addTable') || 'Add Table'}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsAddingMultipleTables(true);
                        setIsAddingTable(false);
                      }}
                      disabled={isAddingTable || isAddingMultipleTables}
                      className="w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4 me-2" />
                      <span className="truncate">
                        {t('settings.addMultipleTables') || 'Add Multiple Tables'}
                      </span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingTables ? (
                  <LoadingSpinner />
                ) : (
                  <div className="space-y-4">
                    {/* Add Table Form */}
                    {isAddingTable && (
                      <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
                              <Plus className="w-5 h-5 text-primary" />
                            </div>
                            <h3 className="font-semibold text-lg">{t('settings.addTable')}</h3>
                          </div>
                          <div className="grid gap-4 md:grid-cols-4">
                            <div className="space-y-2">
                              <Label htmlFor="newTableNumber">{t('table.tableNumber')}</Label>
                              <Input
                                id="newTableNumber"
                                type="number"
                                min="1"
                                value={tableForm.tableNumber}
                                onChange={(e) =>
                                  setTableForm({ ...tableForm, tableNumber: e.target.value })
                                }
                                placeholder="1"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="newTableSeats">{t('table.seats')}</Label>
                              <Input
                                id="newTableSeats"
                                type="number"
                                min="1"
                                value={tableForm.seats}
                                onChange={(e) =>
                                  setTableForm({ ...tableForm, seats: e.target.value })
                                }
                                placeholder="4"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="newTableZone">{t('table.zone') || 'Zone'}</Label>
                              <Select
                                value={tableForm.zoneId || 'all'}
                                onValueChange={(value) =>
                                  setTableForm({
                                    ...tableForm,
                                    zoneId: value === 'all' ? '' : value,
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t('settings.allZones') || 'All Zones'}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">
                                    {t('settings.allZones') || 'All Zones'}
                                  </SelectItem>
                                  {zones
                                    ?.filter((z) => z.isActive)
                                    .map((zone) => (
                                      <SelectItem key={zone.id} value={zone.id}>
                                        {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2">
                              <Button
                                onClick={handleAddTable}
                                disabled={
                                  createTable.isPending ||
                                  !tableForm.tableNumber ||
                                  !tableForm.seats
                                }
                                className="flex-1"
                              >
                                <Save className="w-4 h-4 sm:me-2" />
                                <span className="hidden sm:inline">{t('common.save')}</span>
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setIsAddingTable(false);
                                  setTableForm({ tableNumber: '', seats: '', zoneId: '' });
                                }}
                                disabled={createTable.isPending}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Add Multiple Tables Form */}
                    {isAddingMultipleTables && (
                      <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
                              <Plus className="w-5 h-5 text-primary" />
                            </div>
                            <h3 className="font-semibold text-lg">
                              {t('settings.addMultipleTables')}
                            </h3>
                          </div>
                          <div className="grid gap-4 md:grid-cols-5">
                            <div className="space-y-2">
                              <Label htmlFor="startNumber">
                                {t('settings.startTableNumber') || 'Start Number'}
                              </Label>
                              <Input
                                id="startNumber"
                                type="number"
                                min="1"
                                value={bulkTableForm.startNumber}
                                onChange={(e) =>
                                  setBulkTableForm({
                                    ...bulkTableForm,
                                    startNumber: e.target.value,
                                  })
                                }
                                placeholder="1"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="endNumber">
                                {t('settings.endTableNumber') || 'End Number'}
                              </Label>
                              <Input
                                id="endNumber"
                                type="number"
                                min="1"
                                value={bulkTableForm.endNumber}
                                onChange={(e) =>
                                  setBulkTableForm({ ...bulkTableForm, endNumber: e.target.value })
                                }
                                placeholder="10"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="bulkTableSeats">{t('table.seats')}</Label>
                              <Input
                                id="bulkTableSeats"
                                type="number"
                                min="1"
                                value={bulkTableForm.seats}
                                onChange={(e) =>
                                  setBulkTableForm({ ...bulkTableForm, seats: e.target.value })
                                }
                                placeholder="4"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="bulkTableZone">{t('table.zone') || 'Zone'}</Label>
                              <Select
                                value={bulkTableForm.zoneId || 'all'}
                                onValueChange={(value) =>
                                  setBulkTableForm({
                                    ...bulkTableForm,
                                    zoneId: value === 'all' ? '' : value,
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t('settings.allZones') || 'All Zones'}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">
                                    {t('settings.allZones') || 'All Zones'}
                                  </SelectItem>
                                  {zones
                                    ?.filter((z) => z.isActive)
                                    .map((zone) => (
                                      <SelectItem key={zone.id} value={zone.id}>
                                        {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2">
                              <Button
                                onClick={handleAddMultipleTables}
                                disabled={
                                  createMultipleTables.isPending ||
                                  !bulkTableForm.startNumber ||
                                  !bulkTableForm.endNumber ||
                                  !bulkTableForm.seats
                                }
                                className="flex-1"
                              >
                                <Save className="w-4 h-4 sm:me-2" />
                                <span className="hidden sm:inline">{t('common.save')}</span>
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setIsAddingMultipleTables(false);
                                  setBulkTableForm({
                                    startNumber: '',
                                    endNumber: '',
                                    seats: '',
                                    zoneId: '',
                                  });
                                }}
                                disabled={createMultipleTables.isPending}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Tables List */}
                    {!tables || tables.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="flex justify-center mb-4">
                          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                            <Utensils className="w-8 h-8 text-muted-foreground" />
                          </div>
                        </div>
                        <p className="text-muted-foreground">
                          {t('settings.noTables') || 'No tables yet. Add your first table above.'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Mobile View - Cards */}
                        <div className="grid gap-4 sm:grid-cols-2 md:hidden">
                          {tables.map((table) => (
                            <Card key={table.id} className="hover:shadow-lg transition-shadow">
                              <CardContent className="pt-6">
                                {editingTableId === table.id ? (
                                  <div className="space-y-4">
                                    <div className="space-y-2">
                                      <Label>{t('table.tableNumber')}</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={tableForm.tableNumber}
                                        onChange={(e) =>
                                          setTableForm({
                                            ...tableForm,
                                            tableNumber: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>{t('table.seats')}</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={tableForm.seats}
                                        onChange={(e) =>
                                          setTableForm({ ...tableForm, seats: e.target.value })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>{t('table.zone') || 'Zone'}</Label>
                                      <Select
                                        value={tableForm.zoneId || 'all'}
                                        onValueChange={(value) =>
                                          setTableForm({
                                            ...tableForm,
                                            zoneId: value === 'all' ? '' : value,
                                          })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue
                                            placeholder={t('settings.allZones') || 'All Zones'}
                                          />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="all">
                                            {t('settings.allZones') || 'All Zones'}
                                          </SelectItem>
                                          {zones
                                            ?.filter((z) => z.isActive)
                                            .map((zone) => (
                                              <SelectItem key={zone.id} value={zone.id}>
                                                {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                                              </SelectItem>
                                            ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveTable(table.id)}
                                        disabled={
                                          updateTable.isPending ||
                                          !tableForm.tableNumber ||
                                          !tableForm.seats
                                        }
                                        className="flex-1"
                                      >
                                        <Save className="w-4 h-4 sm:me-2" />
                                        <span className="hidden sm:inline">{t('common.save')}</span>
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleCancelTableEdit}
                                        disabled={updateTable.isPending}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-4">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                                            <Utensils className="w-6 h-6 text-primary" />
                                          </div>
                                          <div>
                                            <div className="text-2xl font-bold">
                                              {t('table.table')} {table.tableNumber}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-muted-foreground mb-2">
                                          <Users className="w-4 h-4" />
                                          <span className="text-sm">
                                            {table.seats} {t('table.seats')}
                                          </span>
                                        </div>
                                        {table.zone && (
                                          <div className="text-sm text-muted-foreground">
                                            {t('table.zone') || 'Zone'}:{' '}
                                            {i18n.language === 'ar'
                                              ? table.zone.nameAr
                                              : table.zone.nameFr}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-2 pt-2 border-t">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditTable(table)}
                                        className="flex-1"
                                      >
                                        <Pencil className="w-4 h-4 sm:me-2" />
                                        <span className="hidden sm:inline">{t('common.edit')}</span>
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDeleteTable(table.id)}
                                        disabled={deleteTable.isPending}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>

                        {/* Desktop View - Table */}
                        <div className="hidden md:block border rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="min-w-[100px]">
                                    {t('table.tableNumber')}
                                  </TableHead>
                                  <TableHead className="min-w-[120px]">
                                    {t('table.seats')}
                                  </TableHead>
                                  <TableHead className="min-w-[150px]">
                                    {t('table.zone') || 'Zone'}
                                  </TableHead>
                                  <TableHead className="min-w-[200px] text-right">
                                    {t('common.actions')}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tables.map((table) => (
                                  <TableRow key={table.id}>
                                    <TableCell className="font-medium">
                                      {editingTableId === table.id ? (
                                        <Input
                                          type="number"
                                          min="1"
                                          value={tableForm.tableNumber}
                                          onChange={(e) =>
                                            setTableForm({
                                              ...tableForm,
                                              tableNumber: e.target.value,
                                            })
                                          }
                                          className="w-24"
                                        />
                                      ) : (
                                        <div className="flex items-center gap-2">
                                          <Utensils className="w-4 h-4 text-primary" />
                                          {t('table.table')} {table.tableNumber}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {editingTableId === table.id ? (
                                        <Input
                                          type="number"
                                          min="1"
                                          value={tableForm.seats}
                                          onChange={(e) =>
                                            setTableForm({ ...tableForm, seats: e.target.value })
                                          }
                                          className="w-24"
                                        />
                                      ) : (
                                        <div className="flex items-center gap-2">
                                          <Users className="w-4 h-4 text-muted-foreground" />
                                          {table.seats} {t('table.seats')}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {editingTableId === table.id ? (
                                        <Select
                                          value={tableForm.zoneId || 'all'}
                                          onValueChange={(value) =>
                                            setTableForm({
                                              ...tableForm,
                                              zoneId: value === 'all' ? '' : value,
                                            })
                                          }
                                        >
                                          <SelectTrigger className="w-40">
                                            <SelectValue
                                              placeholder={t('settings.allZones') || 'All Zones'}
                                            />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="all">
                                              {t('settings.allZones') || 'All Zones'}
                                            </SelectItem>
                                            {zones
                                              ?.filter((z) => z.isActive)
                                              .map((zone) => (
                                                <SelectItem key={zone.id} value={zone.id}>
                                                  {i18n.language === 'ar'
                                                    ? zone.nameAr
                                                    : zone.nameFr}
                                                </SelectItem>
                                              ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <div className="text-sm">
                                          {table.zone
                                            ? i18n.language === 'ar'
                                              ? table.zone.nameAr
                                              : table.zone.nameFr
                                            : t('settings.allZones') || 'All Zones'}
                                        </div>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {editingTableId === table.id ? (
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            size="sm"
                                            onClick={() => handleSaveTable(table.id)}
                                            disabled={
                                              updateTable.isPending ||
                                              !tableForm.tableNumber ||
                                              !tableForm.seats
                                            }
                                          >
                                            <Save className="w-4 h-4 sm:me-2" />
                                            <span className="hidden sm:inline">
                                              {t('common.save')}
                                            </span>
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleCancelTableEdit}
                                            disabled={updateTable.isPending}
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleEditTable(table)}
                                          >
                                            <Pencil className="w-4 h-4 sm:me-2" />
                                            <span className="hidden sm:inline">
                                              {t('common.edit')}
                                            </span>
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleDeleteTable(table.id)}
                                            disabled={deleteTable.isPending}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Confirm Dialog */}
        <ConfirmModal
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmLabel={t('common.delete') || 'Delete'}
          cancelLabel={t('common.cancel') || 'Cancel'}
          variant="destructive"
        />

        {/* Alert Dialog */}
        <AlertModal
          open={alertDialog.open}
          onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
          title={alertDialog.title}
          description={alertDialog.description}
          actionLabel={t('common.ok') || 'OK'}
        />
      </div>
    </ProtectedRoute>
  );
}
